import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Calendar, Clock, MapPin, Trophy, Users, Share2, CheckCircle, Plus, HelpCircle, X } from "lucide-react";
import { Event, EventOption } from "@/types/event";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface EventResultsProps {
  event: Event;
  onBack: () => void;
}

export const EventResults: React.FC<EventResultsProps> = ({ event, onBack }) => {
  const [guestNames, setGuestNames] = useState<{[guestId: string]: string}>({});
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Create a mapping of guest IDs to names for displaying voter names
    const nameMapping: {[guestId: string]: string} = {};
    event.guests.forEach(guest => {
      nameMapping[guest.id] = guest.name || guest.email || guest.phone || 'Anonymous';
    });
    setGuestNames(nameMapping);
  }, [event.guests]);

  // Use real data from the event
  const totalVoters = event.totalVoters;
  const optionsWithVotes = event.options.map(option => {
    const maybeVoters = event.votes
      .filter(v => v.optionId === option.id && v.voteType === 'maybe')
      .map(v => v.guestId);
    const unavailableVoters = event.votes
      .filter(v => v.optionId === option.id && v.voteType === 'unavailable')
      .map(v => v.guestId);
    return {
      ...option,
      voterNames: option.voters.map(voterId => guestNames[voterId] || 'Anonymous'),
      maybeCount: maybeVoters.length,
      maybeNames: maybeVoters.map(id => guestNames[id] || 'Anonymous'),
      unavailableCount: unavailableVoters.length,
    };
  });

  const getIconForOption = (type: string) => {
    switch (type) {
      case 'date':
        return <Calendar className="w-4 h-4" />;
      case 'time':
        return <Clock className="w-4 h-4" />;
      case 'location':
        return <MapPin className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const formatOptionValue = (option: EventOption) => {
    switch (option.type) {
      case 'date':
        return new Date(option.value).toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'time':
        return new Date(`2000-01-01T${option.value}`).toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case 'location':
        return option.value;
      default:
        return option.value;
    }
  };

  const groupedOptions = {
    date: optionsWithVotes.filter(opt => opt.type === 'date'),
    time: optionsWithVotes.filter(opt => opt.type === 'time'),
    location: optionsWithVotes.filter(opt => opt.type === 'location'),
  };

  const getWinnerForType = (type: 'date' | 'time' | 'location') => {
    const options = groupedOptions[type];
    if (options.length === 0) return null;
    return options.reduce((winner, option) => 
      option.votes > winner.votes ? option : winner
    );
  };

  const finalEvent = {
    date: getWinnerForType('date'),
    time: getWinnerForType('time'),
    location: getWinnerForType('location')
  };

  // Handler functions for the action buttons
  const handleShareResults = async () => {
    try {
      const shareableUrl = `${window.location.origin}/vote/${event.shareableLink}`;
      await navigator.clipboard.writeText(shareableUrl);
      toast({
        title: "Link copied",
        description: "Results link has been copied to your clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleExportToCalendar = () => {
    if (!finalEvent.date) {
      toast({
        title: "No date available",
        description: "This event doesn't have any date options to add to calendar.",
        variant: "destructive",
      });
      return;
    }

    // Create calendar event with the winning options
    const eventDate = new Date(finalEvent.date.value);
    if (finalEvent.time) {
      const [hours, minutes] = finalEvent.time.value.split(':');
      eventDate.setHours(parseInt(hours), parseInt(minutes));
    }

    const endDate = new Date(eventDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours duration

    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Event Voting App//Event//EN',
      'BEGIN:VEVENT',
      `DTSTART:${formatDate(eventDate)}`,
      `DTEND:${formatDate(endDate)}`,
      `SUMMARY:${event.name}`,
      `DESCRIPTION:${event.description || 'Event created through Event Voting App'}`,
      finalEvent.location ? `LOCATION:${finalEvent.location.value}` : '',
      `ORGANIZER:CN=${event.hostName}`,
      `UID:${event.id}@eventvoting.app`,
      'STATUS:CONFIRMED',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Event reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line).join('\r\n');

    try {
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Calendar file downloaded",
        description: "The .ics file has been downloaded. Import it to your calendar app.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate calendar file.",
        variant: "destructive",
      });
    }
  };

  const handleCreateNewEvent = () => {
    navigate('/create');
  };

  return (
    <div className="min-h-screen bg-gradient-background">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">

        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-8">
          {/* Event Header */}
          <Card className="shadow-medium border-0">
            <CardHeader className="text-center p-4 sm:p-6">
              <CardTitle className="text-xl sm:text-2xl md:text-3xl bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {event.name} - Results
              </CardTitle>
              <CardDescription className="text-base sm:text-lg mt-1 sm:mt-2">
                Hosted by {event.hostName}
              </CardDescription>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  <span className="font-semibold text-sm sm:text-base">{totalVoters} votes collected</span>
                </div>
                <Badge variant="secondary" className="bg-accent/20 text-accent-foreground text-xs sm:text-sm">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Voting Complete
                </Badge>
              </div>
            </CardHeader>
          </Card>

          {/* Final Event Summary */}
          <Card className="shadow-medium border-0 bg-gradient-success text-accent-foreground">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-white text-lg sm:text-xl">
                <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
                Final Event Details
              </CardTitle>
              <CardDescription className="text-accent-foreground/80 text-sm sm:text-base">
                Based on the majority votes from your guests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
              {finalEvent.date && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <div className="text-sm sm:text-base break-words">
                    <span className="font-semibold">Date: </span>
                    {formatOptionValue(finalEvent.date)}
                  </div>
                </div>
              )}
              {finalEvent.time && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <div className="text-sm sm:text-base">
                    <span className="font-semibold">Time: </span>
                    {formatOptionValue(finalEvent.time)}
                  </div>
                </div>
              )}
              {finalEvent.location && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <div className="text-sm sm:text-base break-words">
                    <span className="font-semibold">Location: </span>
                    {formatOptionValue(finalEvent.location)}
                  </div>
                </div>
              )}
              <div className="pt-2 sm:pt-4">
                <Button variant="secondary" className="bg-white/20 text-white hover:bg-white/30 w-full sm:w-auto touch-manipulation">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Final Details
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Results */}
          {Object.entries(groupedOptions).map(([type, options]) => {
            if (options.length === 0) return null;
            
            const winner = getWinnerForType(type as 'date' | 'time' | 'location');
            
            return (
              <Card key={type} className="shadow-medium border-0">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 capitalize text-base sm:text-lg">
                    {getIconForOption(type)}
                    {type} Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6">
                  {options
                    .sort((a, b) => b.votes - a.votes)
                    .map((option, index) => {
                      const percentage = totalVoters > 0 ? Math.round((option.votes / totalVoters) * 100) : 0;
                      const isWinner = option.id === winner?.id;
                      
                      return (
                        <div
                          key={option.id}
                          className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 ${
                            isWinner 
                              ? 'border-accent bg-accent/10' 
                              : 'border-border'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 mb-3">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                              {isWinner && <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-accent shrink-0" />}
                              <span className="font-semibold text-sm sm:text-base break-words">
                                {formatOptionValue(option)}
                              </span>
                              {isWinner && (
                                <Badge variant="secondary" className="bg-accent/20 text-accent-foreground text-xs shrink-0">
                                  Winner
                                </Badge>
                              )}
                            </div>
                            <div className="text-left sm:text-right shrink-0">
                              <div className="font-bold text-base sm:text-lg">{option.votes}</div>
                              <div className="text-xs sm:text-sm text-muted-foreground">
                                {percentage}% preferred
                              </div>
                              {(option.maybeCount > 0 || option.unavailableCount > 0) && (
                                <div className="flex flex-wrap gap-1 mt-1 sm:justify-end">
                                  {option.maybeCount > 0 && (
                                    <Badge className="text-[10px] bg-warning/15 text-warning-foreground border border-warning/40">
                                      <HelpCircle className="w-3 h-3 mr-0.5" />
                                      {option.maybeCount} maybe
                                    </Badge>
                                  )}
                                  {option.unavailableCount > 0 && (
                                    <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">
                                      <X className="w-3 h-3 mr-0.5" />
                                      {option.unavailableCount}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <Progress 
                            value={percentage} 
                            className="h-2 mb-3" 
                          />
                          
                          <div className="flex flex-wrap gap-1">
                            {option.voterNames.slice(0, 3).map((voterName, voterIndex) => (
                              <Badge key={voterIndex} variant="outline" className="text-xs">
                                {voterName}
                              </Badge>
                            ))}
                            {option.voterNames.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{option.voterNames.length - 3} more
                              </Badge>
                            )}
                            {option.maybeNames.slice(0, 2).map((n, i) => (
                              <Badge key={`m-${i}`} className="text-xs bg-warning/10 text-warning-foreground border border-warning/30">
                                <HelpCircle className="w-3 h-3 mr-0.5" />{n}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                </CardContent>
              </Card>
            );
          })}

          {/* Action Buttons */}
          <Card className="shadow-medium border-0">
            <CardContent className="p-4 sm:pt-6">
              <div className="flex flex-col gap-3 sm:gap-4">
                <Button className="w-full touch-manipulation" size="lg" onClick={handleShareResults}>
                  <Share2 className="w-4 h-4 mr-2" />
                  <span className="text-sm sm:text-base">Share Results</span>
                </Button>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <Button variant="outline" className="w-full touch-manipulation" size="lg" onClick={handleExportToCalendar}>
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Export to Calendar</span>
                  </Button>
                  <Button variant="outline" className="w-full touch-manipulation" size="lg" onClick={handleCreateNewEvent}>
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="text-sm sm:text-base">Create New Event</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};